/** The rotation assistant (M18, ADR-108): who could go on which job of a week, so that the club's tasks turn among its people. It is ADVICE for the head greenkeeper, computed
 *  on the device each time it is shown from what the server says (`api.plan_rotation_inputs`: who is there, who may do which job with which machine, what each person did over
 *  the last weeks) and never stored. Nothing is saved until the planner accepts a line; each accepted line is an ordinary `assignment.create`, which the server checks again
 *  (qualifications, a machine out of service). Pure: no I/O, no clock, no randomness; the same inputs give the same proposals.
 *
 *  The rule, in the order it decides (shown on screen word for word):
 *   1. only people whose fonction on the week is in the rotation (Réglages › Fonctions, M23 item 28: by default every fonction but Superintendent and Mécanicien; somebody
 *      with no fonction is out) or whom the planner ticked for this week, and not left out by the planner, with a working role, present that day (not "off"), not already
 *      on that job, not already on another job of the same day and group, and allowed to do the job (with a free machine of its type that they may drive, when its
 *      template names machine types);
 *   2. present all day before partly present;
 *   3. the one who did this kind of task the fewest times (over the history period, plus what is already planned or proposed from today on);
 *   4. then the one who did it longest ago (never comes first);
 *   5. then the one with the fewest jobs this week;
 *   6. then by name.
 *  A machine is proposed only when the job has candidate machines (its template's machine types, or else the five machines most used for this kind of task in the history): the free
 *  one (not out of service, not on another job of the same day and group) that the person may drive, most used for this task first, then most used by this person, then by tag.
 *  The history is what happened before today (the server cuts it there); a line only planned, this week or between today and the week, counts in rule 3 and 4 as planned.
 *  A day before a person's first day or after their last one at the site (`employed_from` / `employed_until`, M20) is a day they are not present: rule 1 treats it as
 *  "off" (no step of its own, and the unfilled seats count them as absent). */

/** What `api.plan_rotation_inputs` returns (jsonb). Days are site-local ISO dates. */
export interface RotationInputs {
  /** the history is [history_from, history_to): before the week and before today */
  from: string; to: string; today: string; history_from: string; history_to: string; history_days: number
  /** the people of the site in the week (a membership there overlaps it), with their roles in the week and the first and last day of those memberships (last: null while
   *  one is open) */
  people: Array<{ person_id: string; display_name: string; roles: string[]; employed_from: string | null; employed_until: string | null }>
  availability: Array<{ person_id: string; local_date: string; status: 'partial' | 'off' }>
  slots: RotationSlot[]
  /** the week's lines: live assignments of its jobs that were not cancelled (finished ones too), and a work session of a person who was not put on the job */
  assignments: RotationLine[]
  /** lines already planned between today and the week (when the week is further on): they count in the rule, never as work done */
  planned_before: RotationLine[]
  machines: Array<{ asset_id: string; readable_tag: string; model: string | null; asset_type_code: string | null; kind: string; blocked: boolean }>
  history: Array<{ person_id: string; task_type_code: string; n: number; last_date: string }>
  machine_history: Array<{ task_type_code: string; asset_id: string; n: number; last_date: string }>
  person_machine: Array<{ person_id: string; asset_id: string; n: number }>
}
export interface RotationLine { job_id: string; person_id: string; asset_id: string | null; local_date: string; sequence_group: number; task_type_code: string }
export interface RotationSlot {
  job_id: string; local_date: string; sequence_group: number; start_local: string | null; priority: number; task_type_code: string
  title: { fr?: string; en?: string } | null; required_crew: number | null
  /** the live crew already on the job */
  crew: Array<{ person_id: string; asset_id: string | null }>
  /** the template's machine types (empty: the template names none) */
  asset_type_codes: string[]
  /** people with no qualification gap for this job without a machine */
  eligible: string[]
  /** candidate machines, each with the people who have no qualification gap for this job WITH that machine */
  machines: Array<{ asset_id: string; eligible: string[] }>
}

export interface RotationOptions {
  /** the roles whose people go on the board (`WORK_ROLES` of planUi.ts) */
  workRoles: readonly string[]
  /** M23 item 28: the people whose fonction on the week is in the rotation (`titleInRotation` on `fonctionOf`, from the device's mirror). Not given: everyone with a
   *  working role is in, the rule before M23 (the tests of the rule itself use it). */
  byFonction?: ReadonlySet<string> | readonly string[]
  /** people the planner ticked by hand for this week, whatever their fonction says (page state, nothing stored) */
  include?: readonly string[]
  /** people the planner leaves out of this rotation (they keep what they already have); wins over everything */
  exclude?: readonly string[]
}

/** M23 item 28: the fonctions out of the rotation unless the club says otherwise — the Superintendent and the Mécanicien, by their default CODES (the Second, who holds the
 *  Superintendent's access, is in: the owner, 2026-10-23). The same rule as `private.rotation_default` on the server. */
export const ROTATION_OUT_BY_CODE: readonly string[] = ['superintendent', 'mecanicien']
export const inRotationByDefault = (code: string): boolean => !ROTATION_OUT_BY_CODE.includes(code)
/** A fonction's word on the rotation: its own flag, else — a row this device has not pulled since the column came — the rule by code, so that the pool is not emptied
 *  while the device catches up. No fonction (an access login, a contractor): out, until the planner ticks the person for a week. */
export function titleInRotation(title: { code: string; in_rotation?: boolean | null } | null | undefined): boolean {
  if (!title) return false
  return title.in_rotation ?? inRotationByDefault(title.code)
}
/** Who is in the pool before the planner touches anything: every person of the week whose fonction says so (`fonction(id)` is the device's `fonctionOf` on the week). */
export function peopleInRotation(people: ReadonlyArray<{ person_id: string }>, fonction: (personId: string) => { code: string; in_rotation?: boolean | null } | null | undefined): Set<string> {
  return new Set(people.filter((p) => titleInRotation(fonction(p.person_id))).map((p) => p.person_id))
}
/** `template_type` / `usual_for_task`: a machine is proposed (of the template's type / used for this task before); `none_free`: the person may drive some of the usual
 *  machines but every one of them is out of service or on another job of the same day and group; `none_allowed`: the person may drive none of the usual machines (both only for a
 *  job whose template names no machine type, which can be done without one: a job that names types always gets one); `not_needed`: the job has no candidate machine. */
export type MachineReason = 'template_type' | 'usual_for_task' | 'none_free' | 'none_allowed' | 'not_needed'
export interface ProposalReason {
  /** how many times this person did (or is planned or proposed for) this kind of task: history plus this week */
  taskCount: number
  /** days between the last time and the job's day; null: never in the period */
  lastDaysAgo: number | null
  /** jobs of this person in the week, already planned or proposed, before this one */
  weekLoad: number
  /** partly present that day */
  partial: boolean
  machine: MachineReason
  /** how often the proposed machine was used for this kind of task in the history (0 without a machine) */
  machineUses: number
}
export interface Proposal {
  /** `${job_id}:${seat}`, seat counted from 0 among the seats this run fills */
  key: string
  job_id: string; local_date: string; sequence_group: number; task_type_code: string
  person_id: string; asset_id: string | null
  reason: ProposalReason
}
/** A seat nobody could take, with why the others could not (each person counted once, by the first reason in this order: off (a day outside their employment included),
 *  busy, not allowed, no free machine of the template's type that they may drive). People the planner left out are not counted. */
export interface Unfilled { job_id: string; local_date: string; missing: number; off: number; busy: number; unqualified: number; no_machine: number }
export interface RotationResult { proposals: Proposal[]; unfilled: Unfilled[] }

/** Whole days from `from` to `to` (site-local ISO dates), by UTC-noon arithmetic: no clock, no time zone, no daylight-saving hour. */
export function isoDayDiff(from: string, to: string): number {
  return Math.round((utcNoon(to) - utcNoon(from)) / 86_400_000)
}
function utcNoon(day: string): number {
  const [y, m, d] = day.split('-').map(Number) as [number, number, number]
  return Date.UTC(y, m - 1, d, 12)
}

/** Where a site-local day falls in a person's employment at the site: 'before' their first day, 'after' their last one, null inside it (a missing bound is no bound).
 *  ISO dates compare as text. Also the week grid's ("Pas encore arrivé", "Parti"). */
export function outsideEmployment(day: string, first: string | null | undefined, last: string | null | undefined): 'before' | 'after' | null {
  return first && day < first ? 'before' : last && day > last ? 'after' : null
}

const cmp = (a: string, b: string): number => (a < b ? -1 : a > b ? 1 : 0)

/** The order the assistant fills jobs in: day, group, start time (none last), priority (highest first), job id. */
export function compareSlots(a: RotationSlot, b: RotationSlot): number {
  return cmp(a.local_date, b.local_date) || a.sequence_group - b.sequence_group || cmp(a.start_local ?? '99', b.start_local ?? '99')
    || b.priority - a.priority || cmp(a.job_id, b.job_id)
}

/** One line on the week, already planned or proposed by this run. */
interface Taken { job_id: string; person_id: string; asset_id: string | null; local_date: string; sequence_group: number; task_type_code: string }
interface Candidate { person_id: string; display_name: string; partial: boolean; taskCount: number; lastDaysAgo: number | null; weekLoad: number }

const k2 = (a: string, b: string): string => `${a}\u0000${b}`

/** never (null) first, then the longest ago */
function compareLast(a: number | null, b: number | null): number {
  if (a === b) return 0
  if (a === null) return -1
  if (b === null) return 1
  return b - a
}
function compareCandidates(a: Candidate, b: Candidate): number {
  return Number(a.partial) - Number(b.partial) || a.taskCount - b.taskCount || compareLast(a.lastDaysAgo, b.lastDaysAgo) || a.weekLoad - b.weekLoad
    || a.display_name.localeCompare(b.display_name, 'fr') || cmp(a.person_id, b.person_id)
}

export function proposeRotation(inputs: RotationInputs, opts: RotationOptions): RotationResult {
  const workRoles = new Set(opts.workRoles)
  const excluded = new Set(opts.exclude ?? [])
  const included = new Set(opts.include ?? [])
  const byFonction = opts.byFonction === undefined ? null : new Set(opts.byFonction)
  const pool = inputs.people.filter((p) => p.roles.some((r) => workRoles.has(r)) && !excluded.has(p.person_id) && (included.has(p.person_id) || byFonction === null || byFonction.has(p.person_id)))

  const avail = new Map<string, 'partial' | 'off'>()                                   // person, day → status ("off" wins over a second row)
  for (const a of inputs.availability) { const k = k2(a.person_id, a.local_date); if (a.status === 'off' || !avail.has(k)) avail.set(k, a.status) }
  const history = new Map<string, { n: number; last: string }>()                       // person, task type
  for (const h of inputs.history) {
    const k = k2(h.person_id, h.task_type_code); const was = history.get(k)
    history.set(k, was ? { n: was.n + h.n, last: h.last_date > was.last ? h.last_date : was.last } : { n: h.n, last: h.last_date })
  }
  const machineUses = new Map<string, number>()                                        // task type, machine
  for (const m of inputs.machine_history) { const k = k2(m.task_type_code, m.asset_id); machineUses.set(k, (machineUses.get(k) ?? 0) + m.n) }
  const personMachine = new Map<string, number>()                                      // person, machine
  for (const m of inputs.person_machine) { const k = k2(m.person_id, m.asset_id); personMachine.set(k, (personMachine.get(k) ?? 0) + m.n) }
  const machineById = new Map(inputs.machines.map((m) => [m.asset_id, m]))

  const slots = [...inputs.slots].sort(compareSlots)

  // `taken`: what is on the week (existing assignments, then this run's proposals), also indexed by person.
  const taken: Taken[] = []
  const byPerson = new Map<string, Taken[]>()
  const take = (t: Taken): void => { taken.push(t); const l = byPerson.get(t.person_id); if (l) l.push(t); else byPerson.set(t.person_id, [t]) }
  const linesOf = (personId: string): Taken[] => byPerson.get(personId) ?? []
  for (const a of inputs.assignments) {
    take({ job_id: a.job_id, person_id: a.person_id, asset_id: a.asset_id, local_date: a.local_date, sequence_group: a.sequence_group, task_type_code: a.task_type_code })
  }
  // Defensively, a job's crew that the assignments list does not carry (the server sends both from the same rows, so normally nothing is added).
  for (const s of slots) {
    for (const c of s.crew) {
      if (!linesOf(c.person_id).some((t) => t.job_id === s.job_id)) {
        take({ job_id: s.job_id, person_id: c.person_id, asset_id: c.asset_id, local_date: s.local_date, sequence_group: s.sequence_group, task_type_code: s.task_type_code })
      }
    }
  }

  // lines planned between today and the week: they count for rules 3 and 4 (this kind of task, how lately), never for the week's load or a double booking
  const before = new Map<string, RotationLine[]>()
  for (const l of inputs.planned_before ?? []) { const was = before.get(l.person_id); if (was) was.push(l); else before.set(l.person_id, [l]) }

  const proposals: Proposal[] = []
  const unfilled: Unfilled[] = []

  for (const s of slots) {
    const seats = (s.required_crew ?? 1) - s.crew.length
    if (seats <= 0) continue
    const eligible = new Set(s.eligible)
    // `slot.eligible` is judged without a machine, so it leaves out the machine types' own qualifications. A job whose template names machine types is done with one
    // of them: "allowed to do the job" (rule 1) is then "allowed on at least one of its candidate machines", whether that machine is free or not (a taken or out of
    // service machine gives `none_free`, which is true for that person). Without template types the machines are only habit, so nobody is left out for them.
    const machineAllowed = new Set(s.machines.flatMap((c) => c.eligible))
    const needsMachine = s.asset_type_codes.length > 0 && s.machines.length > 0
    let gap: Unfilled | undefined
    // the candidate machines a person may drive that are free now (not out of service, not on a line of the same day and group), best first
    const freeMachines = (personId: string) => s.machines.flatMap((c) => {
      const m = machineById.get(c.asset_id)
      if (!m || m.blocked || !c.eligible.includes(personId)) return []
      if (taken.some((t) => t.asset_id === c.asset_id && t.local_date === s.local_date && t.sequence_group === s.sequence_group)) return []
      return [{ m, forTask: machineUses.get(k2(s.task_type_code, c.asset_id)) ?? 0, byPerson: personMachine.get(k2(personId, c.asset_id)) ?? 0 }]
    }).sort((a, b) => b.forTask - a.forTask || b.byPerson - a.byPerson
      || a.m.readable_tag.localeCompare(b.m.readable_tag, 'fr', { numeric: true }) || cmp(a.m.asset_id, b.m.asset_id))

    for (let seat = 0; seat < seats; seat++) {
      let off = 0, busy = 0, unqualified = 0, noMachine = 0
      const candidates: Candidate[] = []
      for (const p of pool) {
        const status = avail.get(k2(p.person_id, s.local_date))
        if (status === 'off' || outsideEmployment(s.local_date, p.employed_from, p.employed_until)) { off++; continue }
        const mine = linesOf(p.person_id)
        // busy: on this job (its crew is in `taken`, see above) or on another job of the same day and group
        if (mine.some((t) => t.job_id === s.job_id || (t.local_date === s.local_date && t.sequence_group === s.sequence_group))) { busy++; continue }
        if (!eligible.has(p.person_id) || (needsMachine && !machineAllowed.has(p.person_id))) { unqualified++; continue }
        // a job whose template names machine types is done with one of them: no free one this person may drive, no seat for them
        if (needsMachine && freeMachines(p.person_id).length === 0) { noMachine++; continue }
        const h = history.get(k2(p.person_id, s.task_type_code))
        const sameTask = [...mine, ...(before.get(p.person_id) ?? [])].filter((t) => t.task_type_code === s.task_type_code)
        let last: string | null = h?.last ?? null
        for (const t of sameTask) if (t.local_date <= s.local_date && (last === null || t.local_date > last)) last = t.local_date
        candidates.push({
          person_id: p.person_id, display_name: p.display_name, partial: status === 'partial',
          taskCount: (h?.n ?? 0) + sameTask.length, lastDaysAgo: last === null ? null : isoDayDiff(last, s.local_date), weekLoad: mine.length,
        })
      }
      candidates.sort(compareCandidates)
      const best = candidates[0]
      if (!best) {
        if (gap) gap.missing += 1
        else { gap = { job_id: s.job_id, local_date: s.local_date, missing: 1, off, busy, unqualified, no_machine: noMachine }; unfilled.push(gap) }
        continue
      }

      let asset_id: string | null = null
      let machine: MachineReason = 'not_needed'
      let uses = 0
      if (s.machines.length > 0) {
        const chosen = freeMachines(best.person_id)[0]
        if (chosen) { asset_id = chosen.m.asset_id; machine = s.asset_type_codes.length > 0 ? 'template_type' : 'usual_for_task'; uses = chosen.forTask }
        else machine = machineAllowed.has(best.person_id) ? 'none_free' : 'none_allowed'
      }

      proposals.push({
        key: `${s.job_id}:${seat}`, job_id: s.job_id, local_date: s.local_date, sequence_group: s.sequence_group, task_type_code: s.task_type_code,
        person_id: best.person_id, asset_id,
        reason: { taskCount: best.taskCount, lastDaysAgo: best.lastDaysAgo, weekLoad: best.weekLoad, partial: best.partial, machine, machineUses: uses },
      })
      take({ job_id: s.job_id, person_id: best.person_id, asset_id, local_date: s.local_date, sequence_group: s.sequence_group, task_type_code: s.task_type_code })
    }
  }
  return { proposals, unfilled }
}
